import { useEffect, useState, useCallback } from 'react';
import api from '../services/api';
import Modal from '../components/Modal';

const fmt     = (n) => Number(n || 0).toLocaleString('fr-FR');
const fmtDate = (d) => d ? new Date(d).toLocaleDateString('fr-FR', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—';

const STATUS = {
  success:         { label: 'Succès',              color: 'var(--success)', bg: 'var(--success-light)' },
  pending:         { label: 'Paiement en cours',   color: 'var(--warning)', bg: 'var(--warning-light)' },
  failed:          { label: 'Échoué',              color: 'var(--danger)',  bg: 'var(--danger-light)'  },
  awaiting_payment:{ label: 'En attente client',   color: '#7c3aed',        bg: '#f5f3ff'              },
};

const METHODS = {
  orange_money: '🟠 Orange Money',
  mtn_money:    '🟡 MTN Money',
  wave:         '🔵 Wave',
  moov_money:   '🟢 Moov Money',
};

// ── Détail d'une transaction ──────────────────────────────────────────────────
function TxDetail({ tx }) {
  const s = STATUS[tx.status] || STATUS.pending;
  const Row = ({ label, children }) => (
    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', padding: '10px 0', borderBottom: '1px solid var(--border)' }}>
      <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: 0.5 }}>{label}</span>
      <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--text-primary)', textAlign: 'right' }}>{children}</span>
    </div>
  );

  const isAwaiting = tx.status === 'awaiting_payment';

  return (
    <div>
      {/* Statut + référence */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 20, padding: '14px 18px', background: isAwaiting ? '#f5f3ff' : 'var(--surface-2)', borderRadius: 12, border: isAwaiting ? '1.5px solid #ddd6fe' : 'none' }}>
        <div>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>
            {isAwaiting ? 'Accord conclu' : 'Référence'}
          </p>
          {isAwaiting
            ? <p style={{ fontSize: 13, fontWeight: 700, color: '#7c3aed' }}>Accord accepté — paiement commission attendu</p>
            : <code style={{ fontSize: 13, fontWeight: 700, color: 'var(--text-primary)' }}>{tx.payment_reference || '—'}</code>
          }
        </div>
        <span style={{ fontSize: 13, fontWeight: 700, color: s.color, background: s.bg, padding: '6px 14px', borderRadius: 20 }}>{s.label}</span>
      </div>

      {/* Client + Prestataire */}
      <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div style={{ background: '#eff6ff', borderRadius: 12, padding: '14px 16px' }}>
          <p style={{ fontSize: 11, color: '#1e40af', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>🛒 Client</p>
          <p style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>{tx.client_name}</p>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{tx.client_phone}</p>
        </div>
        <div style={{ background: '#fef3c7', borderRadius: 12, padding: '14px 16px' }}>
          <p style={{ fontSize: 11, color: '#92400e', fontWeight: 700, textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 8 }}>🔧 Prestataire</p>
          <p style={{ fontWeight: 700, fontSize: 15, marginBottom: 2 }}>{tx.provider_name}</p>
          <p style={{ fontSize: 12, color: 'var(--text-muted)', fontFamily: 'monospace' }}>{tx.provider_phone}</p>
        </div>
      </div>

      {/* Service */}
      <div style={{ marginBottom: 16, padding: '14px 16px', background: 'var(--surface-2)', borderRadius: 12 }}>
        <p style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 4 }}>Service</p>
        <p style={{ fontWeight: 600, fontSize: 15 }}>{tx.service_title}</p>
      </div>

      {/* Montants */}
      <div style={{ display: 'grid', gridTemplateColumns: isAwaiting ? '1fr 1fr' : '1fr 1fr 1fr', gap: 12, marginBottom: 16 }}>
        <div style={{ padding: '14px 16px', background: 'var(--surface-2)', borderRadius: 12, textAlign: 'center' }}>
          <p style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Prix final</p>
          <p style={{ fontSize: 20, fontWeight: 800, color: 'var(--text-primary)' }}>{fmt(tx.final_price)}</p>
          <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>FCFA</p>
        </div>
        <div style={{ padding: '14px 16px', background: isAwaiting ? '#f5f3ff' : '#fde8ec', borderRadius: 12, textAlign: 'center', border: isAwaiting ? '1.5px dashed #c4b5fd' : 'none' }}>
          <p style={{ fontSize: 11, color: isAwaiting ? '#7c3aed' : 'var(--primary)', textTransform: 'uppercase', letterSpacing: 0.5, fontWeight: 700, marginBottom: 6 }}>
            {isAwaiting ? 'Commission à percevoir' : 'Commission'}
          </p>
          <p style={{ fontSize: 20, fontWeight: 800, color: isAwaiting ? '#7c3aed' : 'var(--primary)' }}>{fmt(tx.amount)}</p>
          <p style={{ fontSize: 11, color: 'var(--text-muted)' }}>FCFA · taux {tx.commission_rate}%</p>
        </div>
        {!isAwaiting && (
          <div style={{ padding: '14px 16px', background: 'var(--surface-2)', borderRadius: 12, textAlign: 'center' }}>
            <p style={{ fontSize: 11, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.5, marginBottom: 6 }}>Méthode</p>
            <p style={{ fontSize: 22, marginBottom: 2 }}>
              {{ orange_money: '🟠', mtn_money: '🟡', wave: '🔵', moov_money: '🟢' }[tx.payment_method] || '💳'}
            </p>
            <p style={{ fontSize: 12, fontWeight: 600 }}>{METHODS[tx.payment_method] || tx.payment_method || '—'}</p>
          </div>
        )}
      </div>

      {/* Méta */}
      <div style={{ background: 'var(--surface-2)', borderRadius: 12, padding: '4px 16px' }}>
        {!isAwaiting && <Row label="ID transaction">#{tx.id}</Row>}
        <Row label="ID négociation">#{tx.negotiation_id}</Row>
        <Row label="Date accord">{fmtDate(tx.created_at)}</Row>
      </div>
    </div>
  );
}

export default function Payments() {
  const [data, setData]       = useState({ transactions: [], total: 0, stats: null });
  const [status, setStatus]   = useState('');
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);

  const load = useCallback(() => {
    setLoading(true);
    const p = new URLSearchParams({ limit: 25 });
    if (status) p.set('status', status);
    api.get(`/admin/payments?${p}`)
      .then(r => setData(r.data))
      .finally(() => setLoading(false));
  }, [status]);

  useEffect(() => { load(); }, [load]);

  const { transactions, stats } = data;

  return (
    <div>
      <div className="page-header">
        <div>
          <h2 className="page-title">Transactions</h2>
          <p className="page-subtitle">Flux de paiement des commissions de mise en relation</p>
        </div>
      </div>

      {/* KPI */}
      {stats && (
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(5,1fr)', gap: 16, marginBottom: 24 }}>
          {[
            { label: 'Total paiements', value: stats.total,            icon: '📋', color: 'var(--info)'    },
            { label: 'Réussis',         value: stats.success,          icon: '✅', color: 'var(--success)' },
            { label: 'En cours',        value: stats.pending,          icon: '⏳', color: 'var(--warning)' },
            { label: 'Accords impayés', value: stats.awaiting_payment, icon: '🤝', color: '#7c3aed'        },
            { label: 'Revenus',         value: `${fmt(stats.revenue)} FCFA`, icon: '💰', color: 'var(--primary)' },
          ].map(k => (
            <div key={k.label} className="card animate-fade" style={{ padding: '20px 24px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: 8 }}>
                <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 600, textTransform: 'uppercase', letterSpacing: '.5px' }}>{k.label}</span>
                <span style={{ fontSize: 20 }}>{k.icon}</span>
              </div>
              <div style={{ fontSize: 26, fontWeight: 800, color: k.color }}>{k.value}</div>
            </div>
          ))}
        </div>
      )}

      {/* Filtres */}
      <div className="card animate-fade" style={{ marginBottom: 20 }}>
        <div className="card-body" style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <span style={{ fontSize: 13, color: 'var(--text-muted)', marginRight: 4 }}>Filtrer :</span>
          {[
            { val: '',                 label: 'Tous' },
            { val: 'success',          label: '✅ Réussis' },
            { val: 'pending',          label: '⏳ Paiement en cours' },
            { val: 'awaiting_payment', label: '🤝 Accord impayé' },
            { val: 'failed',           label: '❌ Échoués' },
          ].map(f => (
            <button key={f.val} onClick={() => setStatus(f.val)} style={{
              padding: '6px 16px', borderRadius: 'var(--radius-full)', fontSize: 13, fontWeight: 600,
              cursor: 'pointer',
              border: status === f.val ? '2px solid var(--primary)' : '1.5px solid var(--border)',
              background: status === f.val ? 'var(--primary-light)' : 'var(--surface)',
              color: status === f.val ? 'var(--primary)' : 'var(--text-secondary)',
            }}>{f.label}</button>
          ))}
          <button onClick={load} style={{ marginLeft: 'auto', background: 'none', border: 'none', cursor: 'pointer', fontSize: 18 }} title="Rafraîchir">🔄</button>
        </div>
      </div>

      {/* Tableau */}
      <div className="card animate-fade">
        <div className="card-header">
          <span className="card-title">Historique ({data.total})</span>
        </div>
        <div style={{ overflowX: 'auto' }}>
          {loading ? (
            <div style={{ padding: 40, textAlign: 'center', color: 'var(--text-muted)' }}>
              <div className="spinner" style={{ margin: '0 auto 12px' }} />Chargement…
            </div>
          ) : transactions.length === 0 ? (
            <div style={{ padding: '48px 24px', textAlign: 'center', color: 'var(--text-muted)' }}>
              <div style={{ fontSize: 40, marginBottom: 12 }}>💳</div>
              <div style={{ fontWeight: 600 }}>Aucune transaction trouvée</div>
            </div>
          ) : (
            <table style={{ width: '100%', borderCollapse: 'collapse' }}>
              <thead>
                <tr style={{ borderBottom: '1px solid var(--border)' }}>
                  {['Service', 'Client', 'Prestataire', 'Méthode', 'Prix final', 'Commission', 'Date', 'Statut'].map(h => (
                    <th key={h} style={{ padding: '12px 16px', textAlign: 'left', fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '.5px', whiteSpace: 'nowrap' }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {transactions.map((tx, idx) => {
                  const s = STATUS[tx.status] || STATUS.pending;
                  const isAwaiting = tx.status === 'awaiting_payment';
                  return (
                    <tr
                      key={tx.id ?? `aw-${tx.negotiation_id}-${idx}`}
                      onClick={() => setSelected(tx)}
                      style={{ borderBottom: '1px solid var(--border)', cursor: 'pointer', transition: 'background 0.1s', background: isAwaiting ? '#faf5ff' : '' }}
                      onMouseEnter={e => e.currentTarget.style.background = isAwaiting ? '#f3e8ff' : 'var(--surface-2)'}
                      onMouseLeave={e => e.currentTarget.style.background = isAwaiting ? '#faf5ff' : ''}
                    >
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontWeight: 600, fontSize: 14, color: 'var(--text-primary)' }}>{tx.service_title}</div>
                        {isAwaiting
                          ? <span style={{ fontSize: 11, color: '#7c3aed', fontWeight: 600 }}>Négo #{tx.negotiation_id}</span>
                          : <code style={{ fontSize: 11, color: 'var(--text-muted)' }}>{tx.payment_reference?.slice(0, 14)}…</code>
                        }
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{tx.client_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{tx.client_phone}</div>
                      </td>
                      <td style={{ padding: '14px 16px' }}>
                        <div style={{ fontSize: 13, fontWeight: 600 }}>{tx.provider_name}</div>
                        <div style={{ fontSize: 12, color: 'var(--text-muted)' }}>{tx.provider_phone}</div>
                      </td>
                      <td style={{ padding: '14px 16px', fontSize: 13 }}>{isAwaiting ? <span style={{ color: '#7c3aed', fontStyle: 'italic' }}>En attente</span> : (METHODS[tx.payment_method] || tx.payment_method || '—')}</td>
                      <td style={{ padding: '14px 16px', fontSize: 14, fontWeight: 700, color: 'var(--text-primary)', whiteSpace: 'nowrap' }}>{fmt(tx.final_price)} FCFA</td>
                      <td style={{ padding: '14px 16px', fontWeight: 700, color: 'var(--primary)', whiteSpace: 'nowrap' }}>{fmt(tx.amount)} FCFA <span style={{ fontSize: 11, color: 'var(--text-muted)', fontWeight: 400 }}>({tx.commission_rate}%)</span></td>
                      <td style={{ padding: '14px 16px', fontSize: 12, color: 'var(--text-muted)', whiteSpace: 'nowrap' }}>{fmtDate(tx.created_at)}</td>
                      <td style={{ padding: '14px 16px' }}>
                        <span style={{ fontSize: 12, fontWeight: 700, color: s.color, background: s.bg, padding: '4px 10px', borderRadius: 'var(--radius-full)' }}>{s.label}</span>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          )}
        </div>
      </div>

      {/* ── Modal détail transaction ── */}
      <Modal
        open={!!selected}
        onClose={() => setSelected(null)}
        title={selected?.status === 'awaiting_payment'
          ? `🤝 Accord conclu — Négo #${selected?.negotiation_id}`
          : `💳 Transaction #${selected?.id ?? ''}`}
        maxWidth={560}
      >
        {selected && <TxDetail tx={selected} />}
      </Modal>
    </div>
  );
}
